import { useQueryParameter } from '@/context/QueryParameter';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

type TViewPaymentDetailProps = () => {
  selectedId: string;
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
  selectedIndex: number | undefined;
  setSelectedIndex: (index: number | undefined) => void;
};

export const useViewPaymentDetail: TViewPaymentDetailProps = () => {
  const [param, setParam] = useQueryParameter();
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    undefined
  );

  /**
   * Store the latest param in a ref to avoid stale closures.
   * useLayoutEffect runs synchronously after DOM updates but before browser paint,
   * ensuring the ref is updated before any effects that might call onDrawerOpen.
   */
  const paramRef = useRef(param);
  useLayoutEffect(() => {
    paramRef.current = param;
  });

  const selectedId = param.get('id') || '';

  const onDrawerOpen = useCallback(
    (id: string) => {
      if (id) {
        // Read from ref to get the latest param value, avoiding stale closure
        const next = new URLSearchParams(paramRef.current);
        next.set('id', id);
        setParam(next, { replace: true });
      }
    },
    [setParam]
  );

  const onDrawerClose = useCallback(() => {
    // Read from ref to get the latest param value, avoiding stale closure
    const next = new URLSearchParams(paramRef.current);
    next.delete('id');
    setParam(next, { replace: true });
  }, [setParam]);

  return {
    selectedId,
    onDrawerOpen,
    onDrawerClose,
    selectedIndex,
    setSelectedIndex,
  };
};
